<template>
  <Builder
    v-if="!loading"
    v-model="toggleValue"
    :settings="settings"
  >
    <template #actions>
      <TemplateActions
        :loading="isLoading"
        @save="onSave"
      />
    </template>
  </Builder>
</template>

<script
  lang="ts"
  setup
>
import { useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { emailTemplateFragment } from '@/graphql-fragments';
import { flash, modal } from '@/helpers/ui';
import Builder from '@/modules/marketing/email/builder/index.vue';
import { useCompanyStore } from '@/stores/company';
import { DispatchTypeEnum } from '@/types';
import { useMarketingStore } from '../../../store';
import { useMarketingBuilderStore } from '../../../store/builder';
import TemplateActions from '../../TemplateActions.vue';
import { useNavBack } from '../../useNavBack';

const { companySettings, updateCompany } = useCompanyStore();
const { t } = useI18n();
const builderStore = useMarketingBuilderStore();
const { setAttrs, updateEmailTemplate } = useMarketingStore();
const { isSubmitted, isInvalid, dispatchType } = storeToRefs(
  useMarketingStore(),
);

const isLoading = ref(false);
const settings = reactive({
  emailType: 'WAITING_LIST',
  title: t('marketing.email.automated_emails.waiting_list.title'),
  description: t('marketing.email.automated_emails.waiting_list.intro'),
  sentDescription: t('marketing.email.automated_emails.waiting_list.when'),
  receiveDescription: t(
    `marketing.email.automated_emails.waiting_list.${dispatchType.value === DispatchTypeEnum.Automatic ? 'automated_receive_info' : 'receive_info'}`,
  ),
  showToggle: true,
  allowImageUpload: false,
});

watch(
  () => dispatchType.value,
  (value: DispatchTypeEnum) => {
    settings.receiveDescription = t(
      `marketing.email.automated_emails.waiting_list.${value === DispatchTypeEnum.Automatic ? 'automated_receive_info' : 'receive_info'}`,
    );
    builderStore.setSettings(settings);
  },
);

const { loading, result, onResult } = useQuery(
  gql`
    query getEmailTemplate($emailType: EmailTemplateEnum!) {
      emailTemplate(emailType: $emailType) {
        ...emailTemplate
      }
      emailTemplateDefault: emailTemplate(
        emailType: $emailType
        defaultValues: true
      ) {
        ...emailTemplate
      }
    }
    ${emailTemplateFragment}
  `,
  {
    emailType: 'WAITING_LIST',
  },
);

onResult(() => {
  if (result.value.emailTemplate) {
    result.value.emailTemplate.emailTemplateDefault =
      result.value.emailTemplateDefault;
    setAttrs(result.value.emailTemplate);
  }
});

const toggleValue = computed({
  get() {
    return companySettings.bookings?.waitingListEnabled || false;
  },
  set(value) {
    const input = {
      settings: {
        bookings: {
          waitingListEnabled: value,
        },
      },
    };

    updateCompany(input).then(() => {
      flash(t(`global.flash.waiting_list_${value ? 'enabled' : 'disabled'}`));
    });
  },
});

const { navBack } = useNavBack();

const onSave = () => {
  isSubmitted.value = true;

  if (isInvalid.value) {
    return;
  }

  modal('confirmation').then(() => {
    isLoading.value = true;
    updateEmailTemplate('WAITING_LIST').finally(() => {
      navBack();
      flash(t('global.flash.email_saved'));
      isSubmitted.value = false;
      isLoading.value = false;
    });
  });
};
</script>
